import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { avatarSrc, squareResize } from '../lib/avatar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initials = (n) => (n || '?').split(' ').filter(Boolean).slice(0, 2).map((x) => x[0]).join('').toUpperCase();

export const AvatarUploadDialog = ({ open, onClose, userId, name, currentUrl, onSaved }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const close = () => { setFile(null); setPreview(null); onClose(); };
  const pick = async (f) => {
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Escolha uma imagem'); return; }
    try { const r = await squareResize(f); setFile(r); setPreview(URL.createObjectURL(r)); } catch { toast.error('Não foi possível ler a imagem'); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await axios.post(`${API}/users/${userId}/avatar`, fd);
      toast.success('Foto atualizada'); onSaved?.(data.avatar_url); close();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao guardar a foto'); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await axios.delete(`${API}/users/${userId}/avatar`); toast.success('Foto removida'); onSaved?.(null); close(); } catch { toast.error('Erro ao remover'); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-sm" data-testid="avatar-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Foto de perfil{name ? ` · ${name}` : ''}</DialogTitle></DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          <Avatar className="h-32 w-32 border-4 border-[#4DD4E8]/30">
            <AvatarImage src={preview || avatarSrc(currentUrl)} className="object-cover" />
            <AvatarFallback className="bg-[#4DD4E8] text-white text-3xl font-semibold">{initials(name)}</AvatarFallback>
          </Avatar>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e.target.files[0])} data-testid="avatar-file-input" />
          <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} className="gap-2" data-testid="avatar-pick-btn"><Camera className="w-4 h-4" />Escolher foto</Button>
          <p className="text-xs text-slate-500 text-center">Uma foto de cara, com boa luz. É cortada em quadrado automaticamente.</p>
        </div>
        <DialogFooter className="gap-2">
          {currentUrl && !file && <Button type="button" variant="ghost" onClick={remove} className="text-rose-600 gap-1" data-testid="avatar-remove-btn"><Trash2 className="w-4 h-4" />Remover</Button>}
          <Button type="button" disabled={!file || busy} onClick={save} className="bg-[#2D3E6F] hover:bg-[#24335c]" data-testid="avatar-save-btn">Guardar foto</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
